import { isEmpty } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import {
  DO_NOT_SERIALIZE,
  type FilterItem,
  type FilterItemSource,
  type FilterViewModel,
} from '@/infra/FiltersViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * Filter that selects exactly one value from a list of options.
 */
export class SingleSelectFilter<TKey, TValue>
  implements FilterViewModel<TKey, TValue>, ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  /** @inheritDoc */
  @observable accessor editing = false

  /**
   * The selected value.
   */
  @observable accessor selected: TValue

  /**
   * The options to select from.
   */
  @observable accessor options: FilterItem<TValue>[] = []

  /**
   * Text the options are filtered by.
   */
  @observable accessor searchText = ''

  /**
   * Loads the options from the item source.
   */
  fetchOptions = task(async (signal?: AbortSignal) => {
    this.setOptions(await this.itemSource(signal))
  })

  /**
   * @param title the filter's title.
   * @param key the field being filtered on.
   * @param _initialValue selected initially, and when cleared.
   * @param itemSource where the options come from.
   * @param _defaultToFirstOption whether activating selects the first option.
   * @param _parseStoredValue parses a deserialized value; `null` falls back to
   *  the initial value.
   */
  constructor(
    public title: string,
    public key: TKey,
    private readonly _initialValue: TValue,
    public itemSource: FilterItemSource<TValue>,
    private readonly _defaultToFirstOption = true,
    private readonly _parseStoredValue?: (value: unknown) => TValue | null,
  ) {
    this.selected = _initialValue
  }

  /** @inheritDoc */
  @computed
  get valid() {
    return !isEmpty(this.value)
  }

  /** @inheritDoc */
  @computed
  get value() {
    return this.selected
  }

  /**
   * Option titles by value.
   */
  @computed
  get optionsMap() {
    return new Map(this.options.map((o) => [o.value, o.title]))
  }

  /**
   * The options matching the search text.
   */
  @computed
  get searchFilterOptions() {
    if (this.searchText.length === 0) {
      return this.options.slice()
    }

    const searchText = this.searchText.toLowerCase()
    return this.options.filter(
      (option) =>
        option.title.toLowerCase().includes(searchText) ||
        option.description?.toLowerCase().includes(searchText),
    )
  }

  /**
   * Loads the options, and selects the first one unless told otherwise. The
   * view renders a failure from `fetchOptions`.
   */
  activate() {
    this._lifecycle.activate()
    void this.fetchOptions(this._lifecycle.signal)
      .then(() => {
        const first = this.options[0]
        if (this._defaultToFirstOption && first) {
          this.change(first.value)
        }
      })
      .catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Reloads the options.
   */
  refresh() {
    return this.fetchOptions()
  }

  /** @inheritDoc */
  @computed
  get valueText() {
    return this.toString()
  }

  /**
   * The selected option's title.
   */
  toString() {
    if (!this.selected) {
      return ''
    }
    return this.optionsMap.get(this.selected) ?? ''
  }

  /**
   * Replaces the options.
   *
   * @param options
   */
  @action.bound
  setOptions(options: FilterItem<TValue>[]) {
    this.options = options
  }

  /**
   * Changes the search text.
   *
   * @param text
   */
  @action.bound
  changeSearchText(text: string) {
    this.searchText = text
  }

  /**
   * Selects the value.
   *
   * @param val
   */
  @action.bound
  change(val: TValue) {
    this.selected = val
  }

  /** @inheritDoc */
  @action.bound
  clearValue() {
    this.selected = this._initialValue
  }

  /** @inheritDoc */
  @action.bound
  startEditing() {
    this.editing = true
  }

  /** @inheritDoc */
  @action.bound
  stopEditing() {
    this.editing = false
  }

  /**
   * Clears the value and closes the editor.
   */
  @action.bound
  clear() {
    this.clearValue()
    this.stopEditing()
  }

  /**
   * Checks whether the value is selected.
   *
   * @param val
   */
  isSelected(val: TValue) {
    return this.selected === val
  }

  /** @inheritDoc */
  serialize() {
    return JSON.stringify(this.selected) || DO_NOT_SERIALIZE
  }

  /** @inheritDoc */
  deserialize(value: unknown) {
    // May behave unexpectedly if existing enum values change.
    try {
      if (typeof value === 'string') {
        const stored: unknown = JSON.parse(value || '{}')
        this.selected = this._parseStoredValue
          ? (this._parseStoredValue(stored) ?? this._initialValue)
          : (stored as TValue)
      }
    } catch {
      this.clearValue()
    }
  }
}
